import { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { countriesAlpha2 } from '@sequencehq/api/commonEnums'
import { CountriesAlpha2 } from '@sequencehq/api/utils/commonEnums'
import { match } from 'ts-pattern'
import {
  isPrimaryBillingContact,
  receivesBillingNotifications,
  withoutPurpose
} from 'CustomerContacts/utils/contactPurposes'

import {
  CustomerFormFieldsData,
  EMPTY_CUSTOMER_FORM_ADDRESS,
  TaxStatus
} from 'Customer/components/CustomerFormFields'
import {
  CustomerInfoChangeField,
  CustomerInfoChangeSection,
  CustomerInfoReviewInput,
  CustomerInfoReviewViewModel,
  CustomerSnapshot
} from './types'

type CustomerContact = Dashboardv99990101CommonModels.Customers.CustomerContact

const getBillingPreferenceLabel = (contact: CustomerContact): string => {
  if (isPrimaryBillingContact(contact)) {
    return 'Primary'
  }

  return receivesBillingNotifications(contact) ? 'Standard' : 'None'
}

/**
 * A scalar field on the customer record: how to read it, label it, and — when
 * editable — how to write an amended value back onto the record. Fields without
 * a `set` are read-only because their display labels can't be safely mapped back
 * to the stored value yet (enum/coded values like country and state).
 */
type FieldSpec = {
  key: string
  label: string
  get: (customer: CustomerSnapshot) => string | null
  set?: (customer: CustomerSnapshot, value: string | null) => CustomerSnapshot
}

/**
 * The address a customer who has none starts from, so patching one field
 * yields a whole address rather than a lone key. Its blank country is not a
 * real one; the operator picks it on the form.
 */
const EMPTY_ADDRESS: NonNullable<CustomerSnapshot['address']> = {
  line1: '',
  town: '',
  postcode: '',
  country: '' as CountriesAlpha2
}

/**
 * Merges a single amended field onto the customer's address, starting from a
 * blank one when the customer has no address yet — otherwise patching `line1`
 * alone would produce an address with no town, postcode or country that still
 * claimed to be complete. Only used to mark address fields editable via
 * `Boolean(spec.set)`; the real write happens in `applyFormDataToCustomer`.
 *
 * Nothing calls the setters today — only their presence is read — so this is
 * about keeping the shape honest for whoever wires them up, not fixing a live
 * crash.
 */
const patchAddress = (
  customer: CustomerSnapshot,
  patch: Partial<NonNullable<CustomerSnapshot['address']>>
): CustomerSnapshot => ({
  ...customer,
  address: { ...(customer.address ?? EMPTY_ADDRESS), ...patch }
})

// Field labels mirror the customer edit form (`CustomerFormFields`) so the
// review reads consistently with the rest of the app.
//
// Only the legal name is diffed here: it's the one company detail the review UI
// surfaces — shown in the before/after card and editable on the customer step.
// Alias, website, domain and tax status are deliberately excluded: this flow
// has no field to display or amend them, so diffing them would open a "Company
// details" review step with nothing to review, and `applyFormDataToCustomer`
// would carry the agent's unreviewed proposal straight through on confirm — a
// change approved blind. Excluding them keeps those proposals out of both the
// review and the submitted record (they're preserved from the original snapshot
// instead).
const DETAIL_FIELDS: FieldSpec[] = [
  {
    // Editable on the customer step of the review, alongside the billing
    // address. The agent's proposal seeds the form (and is shown in the
    // before/after card); the operator can amend it before confirming.
    key: 'legalName',
    // Labelled "Company name" to match the edit form (`CustomerFormFields`),
    // which the before/after card sits beside in the review modal.
    label: 'Company name',
    get: c => normalize(c.legalName),
    set: (c, v) => ({ ...c, legalName: v ?? '' })
  }
]

const ADDRESS_FIELDS: FieldSpec[] = [
  {
    key: 'address.line1',
    label: 'Address line 1',
    get: c => normalize(c.address?.line1),
    set: (c, v) => patchAddress(c, { line1: v ?? '' })
  },
  {
    key: 'address.line2',
    label: 'Address line 2',
    get: c => normalize(c.address?.line2),
    set: (c, v) => patchAddress(c, { line2: v ?? undefined })
  },
  {
    key: 'address.town',
    label: 'City/town',
    get: c => normalize(c.address?.town),
    set: (c, v) => patchAddress(c, { town: v ?? '' })
  },
  {
    // State is a coded value (like country), so it is shown read-only rather
    // than free-text editable.
    key: 'address.state',
    label: 'State',
    get: c => normalize(c.address?.state)
  },
  {
    key: 'address.postcode',
    label: 'Postcode',
    get: c => normalize(c.address?.postcode),
    set: (c, v) => patchAddress(c, { postcode: v ?? '' })
  },
  {
    key: 'address.country',
    label: 'Country',
    get: c =>
      c.address?.country
        ? (countriesAlpha2[c.address.country] ?? c.address.country)
        : null
  }
]

/**
 * Maps a raw task input to the view model the review components consume: the
 * customer identity (read from the `after` snapshot), the agent summary, the
 * changed-only diff sections, and the `after` snapshot edits are applied onto.
 *
 * Contact ids are backfilled first (see `withBackfilledContactIds`) so every
 * consumer downstream keys off the same, always-present id.
 */
export const mapInputToViewModel = (
  input: CustomerInfoReviewInput
): CustomerInfoReviewViewModel => {
  const before = withBackfilledContactIds(input.before)
  const after = withBackfilledContactIds(input.after)

  return {
    customer: {
      id: after.id,
      legalName: after.legalName,
      domain: after.domain
    },
    summary: input.summary,
    source: input.source,
    sections: buildCustomerInfoSections(before, after),
    before,
    after
  }
}

/** Prefix for the synthetic ids given to contacts the backend sent without one. */
const GENERATED_CONTACT_ID_PREFIX = 'generated-contact-'

/**
 * Guarantees every contact carries a non-empty `id`. `CustomerContact.id` is
 * required on the persisted type, but the agent's proposal can include a newly
 * *added* contact that isn't persisted yet and so arrives without one. The whole
 * review keys contacts by id — the diff pairs before/after by id, the shared form
 * uses it as its React key and to match edits, and the preview rings the focused
 * contact's card by id — so a missing id breaks all of them (the added contact
 * can't be ringed, and two id-less contacts would edit as a single one). Backfill
 * a stable id derived from the contact's position, so it's consistent across
 * renders (a random id would reshuffle every render). Added contacts only ever
 * appear in `after`, so a synthetic id never collides with a real `before` id and
 * the contact still diffs correctly as new.
 */
const withBackfilledContactIds = (
  customer: CustomerSnapshot
): CustomerSnapshot => ({
  ...customer,
  contacts: (customer.contacts ?? []).map((contact, index) =>
    contact.id
      ? contact
      : { ...contact, id: `${GENERATED_CONTACT_ID_PREFIX}${index}` }
  )
})

/**
 * Diffs two customer-record snapshots into the sections of changed fields the
 * review UI renders. Only fields that actually changed are included; sections
 * with no changes are dropped entirely. Pure function.
 */
export const buildCustomerInfoSections = (
  before: CustomerSnapshot,
  after: CustomerSnapshot
): CustomerInfoChangeSection[] =>
  [
    {
      key: 'details',
      title: 'Company details',
      fields: diffFields(before, after, DETAIL_FIELDS)
    },
    {
      key: 'billingAddress',
      title: 'Billing address',
      fields: diffFields(before, after, ADDRESS_FIELDS)
    },
    {
      key: 'billingContacts',
      title: 'Billing contacts',
      fields: diffContacts(before, after)
    }
  ].filter(section => section.fields.length > 0)

/**
 * The kind of change the agent proposed, derived from which diff sections are
 * present: a change confined to one section is classified as that section, one
 * spanning several is `mixed`. Drives the category-specific copy across the
 * review UI (see `getChangeReviewTitle`, `getChangeSummaryTitle`). To test
 * whether a *given* section changed, check section membership directly instead.
 */
export type CustomerInfoChangeCategory =
  | 'companyDetails'
  | 'billingAddress'
  | 'billingContacts'
  | 'mixed'

export const getCustomerInfoChangeCategory = (
  sections: CustomerInfoChangeSection[]
): CustomerInfoChangeCategory => {
  const onlyChangedKey = sections.length === 1 ? sections[0]?.key : undefined

  return match(onlyChangedKey)
    .with('details', () => 'companyDetails' as const)
    .with('billingAddress', () => 'billingAddress' as const)
    .with('billingContacts', () => 'billingContacts' as const)
    .otherwise(() => 'mixed' as const)
}

/** Timeline "flagged for review" label shown in the preview pane, by category. */
const CHANGE_REVIEW_TITLES: Record<CustomerInfoChangeCategory, string> = {
  companyDetails: 'Company details change flagged for review',
  billingAddress: 'Billing address change flagged for review',
  billingContacts: 'Billing contact change flagged for review',
  mixed: 'Customer details change flagged for review'
}

/** Inbox-row / preview-pane heading (paired with the customer name), by category. */
const CHANGE_SUMMARY_TITLES: Record<CustomerInfoChangeCategory, string> = {
  companyDetails: 'Company details update',
  billingAddress: 'Billing address update',
  billingContacts: 'Billing contact update',
  mixed: 'Billing details update'
}

export const getChangeReviewTitle = (
  sections: CustomerInfoChangeSection[]
): string => CHANGE_REVIEW_TITLES[getCustomerInfoChangeCategory(sections)]

export const getChangeSummaryTitle = (
  sections: CustomerInfoChangeSection[]
): string => CHANGE_SUMMARY_TITLES[getCustomerInfoChangeCategory(sections)]

/**
 * Maps a customer snapshot into the shape the shared `CustomerFormFields`
 * component consumes. Mirrors the mapping used by the Edit Customer drawer so the
 * review form behaves identically to the rest of the app (country-aware address
 * fields, dynamic contacts, tax status). Aliases, custom properties, and tax
 * registration aren't carried on the task input, so they're seeded
 * empty/disabled.
 */
export const customerSnapshotToFormData = (
  customer: CustomerSnapshot
): CustomerFormFieldsData => ({
  legalName: customer.legalName ?? '',
  contacts: (customer.contacts ?? []).map(contact => ({
    id: contact.id,
    name: contact.name ?? '',
    email: contact.email,
    purposes: contact.purposes,
    notificationSubscriptions: contact.notificationSubscriptions
  })),
  address: {
    country: (customer.address?.country ?? '') as CountriesAlpha2 | '',
    line1: customer.address?.line1 ?? '',
    line2: customer.address?.line2,
    town: customer.address?.town ?? '',
    state: customer.address?.state,
    postcode: customer.address?.postcode ?? ''
  },
  shippingAddress: EMPTY_CUSTOMER_FORM_ADDRESS,
  isShippingSameAsBilling: false,
  taxStatus: (customer.taxStatus ?? '') as TaxStatus | '',
  aliases: [''],
  label: customer.label ?? '',
  taxRegistrations: [],
  customProperties: []
})

/**
 * Builds the customer record submitted on confirm: the *original* record with
 * the operator's reviewed edits applied on top — the legal name (editable on the
 * customer step), billing address and billing contacts. Fields this flow can't
 * surface for review (alias, website, domain, tax status) are intentionally left
 * as they were on the original record, never taken from the agent's proposal, so
 * nothing changes that the operator couldn't see. Non-form fields (id, status,
 * organizations, integrations) are identical across the before/after snapshots
 * and preserved too. New contacts have no server timestamps, so they inherit the
 * customer's `createdAt`; the backend assigns real values on save. Pure function.
 */
export const applyFormDataToCustomer = (
  original: CustomerSnapshot,
  data: CustomerFormFieldsData
): CustomerSnapshot => {
  const contactsById = indexById(original.contacts)

  // A blank country means the operator removed the address, or the customer
  // never had one. Either way the result is an explicit null rather than
  // `undefined`: this record is serialised on confirm, and omitting the key
  // would leave the customer's existing address in place instead of clearing
  // it.
  const country = data.address.country

  return {
    ...original,
    legalName: data.legalName.trim(),
    address: country
      ? {
          country: country as CountriesAlpha2,
          line1: data.address.line1.trim(),
          line2: data.address.line2?.trim() || undefined,
          town: data.address.town.trim(),
          state: data.address.state,
          postcode: data.address.postcode.trim()
        }
      : null,
    contacts: data.contacts.map(contact => {
      const existing = contactsById.get(contact.id)
      return {
        ...existing,
        id: contact.id,
        name: contact.name.trim(),
        email: contact.email.trim(),
        purposes: contact.purposes,
        notificationSubscriptions: contact.notificationSubscriptions,
        createdAt: existing?.createdAt ?? original.createdAt,
        updatedAt: existing?.updatedAt ?? original.createdAt
      }
    })
  }
}

const diffFields = (
  before: CustomerSnapshot,
  after: CustomerSnapshot,
  specs: FieldSpec[]
): CustomerInfoChangeField[] =>
  specs
    .map(spec => ({ spec, before: spec.get(before), after: spec.get(after) }))
    .filter(({ before, after }) => before !== after)
    .map(({ spec, before, after }) => ({
      key: spec.key,
      label: spec.label,
      before,
      after,
      editable: Boolean(spec.set)
    }))

const diffContacts = (
  before: CustomerSnapshot,
  after: CustomerSnapshot
): CustomerInfoChangeField[] => {
  const beforeById = indexById(before.contacts)

  // Contacts present only in `before` — proposal removals — are deliberately
  // not diffed: removals aren't supported through this flow (there's no UI to
  // review one) and the contact is preserved on confirm (see
  // `restoreUnchangedContacts`), so surfacing the removal would advertise a
  // change that won't be applied.
  return (after.contacts ?? []).flatMap(afterContact => {
    const beforeContact = beforeById.get(afterContact.id)
    const contactName = afterContact.name ?? beforeContact?.name ?? 'Contact'

    const specs: Array<{
      suffix: string
      label: string
      editable: boolean
      get: (contact?: CustomerContact) => string | null
    }> = [
      {
        suffix: 'name',
        label: `${contactName} · Name`,
        editable: true,
        get: contact => normalize(contact?.name)
      },
      {
        suffix: 'email',
        label: `${contactName} · Email`,
        editable: true,
        get: contact => normalize(contact?.email)
      },
      {
        suffix: 'billingPreference',
        label: `${contactName} · Billing preference`,
        editable: false,
        get: contact => (contact ? getBillingPreferenceLabel(contact) : null)
      }
    ]

    return specs
      .map(spec => ({
        spec,
        before: spec.get(beforeContact),
        after: spec.get(afterContact)
      }))
      .filter(({ before, after }) => before !== after)
      .map(({ spec, before, after }) => ({
        key: `contacts.${afterContact.id}.${spec.suffix}`,
        label: spec.label,
        before,
        after,
        editable: spec.editable
      }))
  })
}

/**
 * Whether a contact differs from its previous version on any field the review
 * surfaces (name, email, billing preference). A missing `before` — a newly added
 * contact — counts as changed. Mirrors the field comparison in `diffContacts`,
 * at contact granularity rather than field granularity.
 */
const hasContactChanged = (
  before: CustomerContact | undefined,
  after: CustomerContact
): boolean =>
  normalize(before?.name) !== normalize(after.name) ||
  normalize(before?.email) !== normalize(after.email) ||
  (before ? getBillingPreferenceLabel(before) : undefined) !==
    getBillingPreferenceLabel(after)

/**
 * Ids of the contacts a change actually touched — those added (absent from
 * `before`) or modified. Computed from the `after` set, so a contact the
 * proposal removed isn't included: removals aren't supported through this flow
 * and are preserved on confirm (see `restoreUnchangedContacts`). Expects the
 * id-backfilled snapshots from `mapInputToViewModel`, so every contact has a
 * stable id to key by. Lets the preview show only the contacts the change
 * affected rather than the customer's whole roster.
 */
export const getChangedContactIds = (
  before: CustomerSnapshot,
  after: CustomerSnapshot
): Set<string> => {
  const beforeById = indexById(before.contacts)

  return new Set(
    (after.contacts ?? [])
      .filter(contact => hasContactChanged(beforeById.get(contact.id), contact))
      .map(contact => contact.id)
  )
}

/**
 * Rebuilds the full billing-contact list to submit from the review form's edited
 * subset. The form only surfaces the contacts a change touched (see
 * `getChangedContactIds`), so the rest of the roster is never shown or edited —
 * but must still be submitted. Each contact of the original record is carried
 * through in place: a changed one is replaced by the operator's edited version
 * (and dropped if the operator removed it in the form), while the rest —
 * including any contact the proposal tried to remove, since removals aren't
 * supported through this flow and are never applied blind — stay as they were
 * on the original record. One exception: when the reviewed subset holds the
 * PRIMARY role, a restored contact still marked PRIMARY (a malformed proposal
 * promoted without demoting) loses the primary role, so the submitted record
 * never carries two primaries. Contacts the operator added (absent from the
 * original roster) are appended. Pure function.
 */
export const restoreUnchangedContacts = (
  original: CustomerSnapshot,
  edited: CustomerSnapshot,
  changedContactIds: Set<string>
): CustomerContact[] => {
  const editedById = indexById(edited.contacts)
  const originalContacts = original.contacts ?? []

  // The reviewed subset owns the PRIMARY role when it holds one: a malformed
  // proposal can promote a contact without demoting the previous primary, which
  // rides along unshown — carried through as-is it would make the submitted
  // record hold two PRIMARY contacts, invisibly to the operator. Demote it
  // instead (mirroring how the form's primary selector demotes the others), so
  // the primary the operator actually reviewed wins.
  const editedHasPrimary = (edited.contacts ?? []).some(isPrimaryBillingContact)
  const withoutStalePrimary = (contact: CustomerContact): CustomerContact =>
    editedHasPrimary && isPrimaryBillingContact(contact)
      ? withoutPurpose(contact, 'BILLING')
      : contact

  const reconciled = originalContacts.flatMap(contact => {
    if (!changedContactIds.has(contact.id)) {
      return [withoutStalePrimary(contact)]
    }
    const editedVersion = editedById.get(contact.id)
    return editedVersion ? [editedVersion] : []
  })

  const originalIds = new Set(originalContacts.map(contact => contact.id))
  const added = (edited.contacts ?? []).filter(
    contact => !originalIds.has(contact.id)
  )

  return [...reconciled, ...added]
}

const indexById = (
  contacts: CustomerContact[] | undefined
): Map<string, CustomerContact> =>
  new Map((contacts ?? []).map(contact => [contact.id, contact]))

/** Treats empty strings and nullish values uniformly as "no value". */
const normalize = (value: string | null | undefined): string | null =>
  value === undefined || value === null || value === '' ? null : value
